import { useState, type FormEvent } from 'react';
import { useAuth } from '@repletoryreach/sdk-auth';
import { isApiError } from '@repletoryreach/sdk-api';
import { Stack, Button, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * The authenticator step after a password, and since 9 October 2026 after Google, Facebook or Apple too: the
 * provider proves who the person is, and the account's own authenticator code is still asked for.
 */
export function TwoFactorForm({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const t = useT();
  const { completeTwoFactor } = useAuth();
  const [code, setCode] = useState('');
  /**
   * A recovery code is alphanumeric, so `inputMode="numeric"` forced the wrong
   * keypad on mobile for the one code a locked-out user actually has
   * (_UI_improvements/tenant/auth-login.md §4 item 5).
   */
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await completeTwoFactor(code.trim());
      onDone();
    } catch (err) {
      setError(isApiError(err) && err.status >= 500 ? t('auth.login.errors.unavailable') : t('auth.login.errors.badCode'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <Stack gap="md">
        <Text role="bodySmall" c="var(--rr-color-text-muted)">
          {t('auth.login.twofa.hint')}
        </Text>
        <TextInput
          label={useRecovery ? t('auth.login.recoveryCodeLabel') : t('auth.login.twofa.form.code')}
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
          autoFocus
          required
          inputMode={useRecovery ? 'text' : 'numeric'}
          autoCapitalize="off"
          autoComplete="one-time-code"
        />
        {error && <Alert color="error" role="alert">{error}</Alert>}
        <Button type="submit" loading={busy} fullWidth>{t('auth.login.twofa.verify')}</Button>
        <Button
          variant="subtle"
          size="xs"
          onClick={() => { setUseRecovery((v) => !v); setCode(''); setError(null); }}
        >
          {useRecovery ? t('auth.login.useAuthCode') : t('auth.login.useRecoveryCode')}
        </Button>
        <Button variant="subtle" size="xs" onClick={onBack}>
          {t('action.back')}
        </Button>
      </Stack>
    </form>
  );
}
